import { useMemo, useSyncExternalStore } from 'react';
import { LayoutGrid, type LucideIcon } from '../icons';
// 활성 별칭 표 — 고정 경로 해석에도 같은 것을 쓴다(findCatalogItem 머리말 참조).
import { ACTIVE_ALIAS_PREFIX } from '../components/NavBar/NavBar';
import { useMenuConfig } from './menu-config';
import { canSeeMenuPath, useIsAdminViewer, useIsRepresentativeViewer } from './menu-visibility';
import { tLayout, getLayoutLang, subscribeLayoutLang } from './layout-i18n';
import { PRIMARY_NAV, SECONDARY_NAV, ICON_BY_NAME } from '../components/NavBar/NavBar';

/**
 * 좌측 레일 메뉴 **카탈로그** — "지금 이 사용자에게 노출되는 메뉴 목록" 한 벌.
 *
 * 즐겨찾기(⑤ 레일 최하단, 2026-08-18)가 "좌측 레일 메뉴의 바로가기" 라서, 등록 후보 목록과
 * 아이콘 해석이 필요하다. 그렇다고 목록을 새로 만들면 **관리자가 메뉴를 껐을 때** 사이드바에는
 * 없는 메뉴가 즐겨찾기 팝업에만 남는다 — AllMenuPanel/AllMenuPopover 머리말이 경고하는 바로
 * 그 갈라짐이다. 그래서 여기서도 SSOT 는 하나다:
 *
 *   1) `useMenuConfig()` — /menu-config 레지스트리 스냅샷(**앱당 1요청** 싱글턴, 추가 fetch 없음).
 *      응답에는 enabled=true 행만 들어온다 = 행이 있다는 것이 곧 노출 허용이다.
 *   2) 레지스트리를 못 읽으면 `NavBar` 의 하드코딩 fallback(PRIMARY_NAV/SECONDARY_NAV) — 배열
 *      자체를 import 해서 쓴다(복제 금지). 의도적 비노출 메뉴(급여·인사·전자결재)는 그 배열에
 *      없으니 여기에도 자동으로 안 나온다.
 *
 * `section='status'` 행은 뺀다 — 하단 상태바 전용 자리라 좌측 레일에도 안 뜨는 항목이다
 * (NavBar 의 rowsToCfg 와 같은 규칙).
 */
export interface MenuCatalogItem {
  /** 이동 경로 — 즐겨찾기의 식별자와 같은 값(FavoriteItem.to). */
  to: string;
  /** 표시 라벨(전체 라벨 — 레일 축약본이 아니다). */
  label: string;
  /** lucide 아이콘. 레지스트리가 custom:* 이면 아래 svg 가 우선한다. */
  icon: LucideIcon;
  /** 커스텀 아이콘(admin_icon_registry) SVG 본문. */
  svg?: string;
  /** 외부 링크 — 새 탭으로 연다. */
  external: boolean;
}

// 보이기 판정은 **제 파일**에 있다 — 여기 두면 NavBar 와 순환 import 가 된다(2026-09-01).
// 옛 import 경로를 깨지 않으려고 그대로 재수출한다.
export {
  ADMIN_ONLY_MENU_PATHS, EXEC_MENU_PATHS, canSeeMenuPath,
  useIsAdminViewer, useIsRepresentativeViewer,
} from './menu-visibility';

/** 레일 메뉴 카탈로그 구독 — 레지스트리 스냅샷이 바뀌면 자동 갱신된다. */
export function useMenuCatalog(): MenuCatalogItem[] {
  const { rows } = useMenuConfig();
  // fallback 라벨은 tLayout 이 해석한다 — 언어가 바뀌면 다시 계산해야 한다(레지스트리 라벨은 무관).
  const lang = useSyncExternalStore(subscribeLayoutLang, getLayoutLang, getLayoutLang);
  const isAdmin = useIsAdminViewer();
  // 「업무보고」는 관리자 **또는 대표** 에게만 — 대표는 역할이 STAFF 일 수 있다.
  const isRep = useIsRepresentativeViewer();
  return useMemo(() => {
    if (rows) {
      return rows
        .filter((r) => r.section !== 'status')
        // 직원에게는 관리자 메뉴를 후보로도 주지 않는다 — 주면 즐겨찾기로 고정할 수 있다.
        .filter((r) => canSeeMenuPath(r.url, isAdmin, isRep))
        .map((r) => ({
          to: r.url,
          // 관리자가 직접 입력한 라벨 — 축약하지 않는다(설정과 화면 불일치 방지).
          label: r.label,
          icon: ICON_BY_NAME[r.icon] ?? LayoutGrid,
          svg: r.svg,
          external: !!r.is_external,
        }));
    }
    return [...PRIMARY_NAV, ...SECONDARY_NAV]
      .filter((i) => canSeeMenuPath(i.to, isAdmin, isRep))
      .map((i) => ({
      to: i.to,
      label: tLayout(i.labelKey),
      icon: i.icon,
      external: false,
    }));
  }, [rows, lang, isAdmin, isRep]);
}

/**
 * 카탈로그에서 경로로 1건 찾기 — 즐겨찾기 행의 아이콘/최신 라벨 해석용. 없으면 null.
 *
 * ── 2026-08-26 — 정확히 같은 경로만 보다가 세 단계로 넓혔다 ────────────────
 * 고정해 둔 경로는 **메뉴 url 보다 오래 산다**. 레지스트리의 url 은 실제로 바뀐 이력이 있고
 * (`/task` → `/project` 등), 못 찾으면 호출부가 «메뉴가 없어졌다» 는 뜻으로 **별 아이콘**을
 * 그린다. 그래서 멀쩡히 살아 있는 메뉴를 고정해 둔 사람이 별을 보게 됐다 —
 * "바로가기를 했다고 해서 아이콘이 별로 나온다"(사용자 보고).
 *
 *   1. 정확히 같은 경로
 *   2. **활성 별칭** — NavBar 가 «이 경로도 그 메뉴 화면이다» 로 쓰는 바로 그 표를 함께 쓴다
 *      (표를 두 벌 두면 한쪽만 고쳐져 갈라진다).
 *   3. **하위 경로** — `/erp/attendance/2026-08` 처럼 안쪽으로 들어간 주소를 고정한 경우.
 *      가장 긴 것부터 골라 `/board` 와 `/board-admin` 같은 곁가지를 잘못 집지 않는다.
 *      외부 링크(`external`)는 제외한다 — 전체 URL 이라 접두 비교가 엉뚱하게 걸린다.
 *
 * 세 단계로도 못 찾으면 그대로 null 이다. 그때의 별은 **정말로 사라진 메뉴**라는 신호로 남는다
 * (조용히 지워 버리면 왜 없어졌는지 알 수 없다).
 */
export function findCatalogItem(catalog: MenuCatalogItem[], to: string): MenuCatalogItem | null {
  const exact = catalog.find((m) => m.to === to);
  if (exact) return exact;

  const underOrEqual = (base: string) => to === base || to.startsWith(`${base}/`);

  const aliased = catalog.find((m) => (ACTIVE_ALIAS_PREFIX[m.to] ?? []).some(underOrEqual));
  if (aliased) return aliased;

  const nested = catalog
    .filter((m) => !m.external && m.to !== '/' && to.startsWith(`${m.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return nested ?? null;
}
